import React, { useState } from 'react';
import { StaticIP, StaticIPCreate, staticIpApi, IPRange } from '../services/api';
import './StaticIPForm.css';

interface StaticIPFormProps {
  ipRangeId: number;
  staticIP?: StaticIP | null;
  ipRange: IPRange;
  existingIPs: string[];
  onClose: () => void;
  onSubmit: () => void;
}

const DEVICE_TYPES = [
  'Server',
  'Workstation',
  'Printer',
  'Network Device',
  'Storage',
  'IoT Device',
  'Other',
];

const StaticIPForm: React.FC<StaticIPFormProps> = ({ 
  ipRangeId, 
  staticIP, 
  ipRange,
  existingIPs,
  onClose, 
  onSubmit 
}) => {
  const [formData, setFormData] = useState<StaticIPCreate>({
    ip_range_id: ipRangeId,
    ip_address: staticIP?.ip_address || '',
    device_name: staticIP?.device_name || '',
    device_type: staticIP?.device_type || '',
    mac_address: staticIP?.mac_address || '',
    description: staticIP?.description || '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateIP = (ip: string): boolean => {
    if (!ip) return false;
    
    const parts = ip.split('.');
    if (parts.length !== 4) return false;
    
    for (const part of parts) {
      const num = parseInt(part);
      if (isNaN(num) || num < 0 || num > 255) return false;
    }
    
    // Check if IP is within the static range
    if (ipRange.static_start && ipRange.static_end) {
      const ipNum = ipToNumber(ip);
      const startNum = ipToNumber(ipRange.static_start);
      const endNum = ipToNumber(ipRange.static_end);
      
      if (ipNum < startNum || ipNum > endNum) {
        setError(`IP must be within range ${ipRange.static_start} - ${ipRange.static_end}`);
        return false;
      }
    }
    
    // Check if IP is already assigned (only for new IPs)
    if (!staticIP && existingIPs.includes(ip)) {
      setError('This IP address is already assigned');
      return false;
    }
    
    return true;
  };

  const ipToNumber = (ip: string): number => {
    const parts = ip.split('.').map(Number);
    return (parts[0] << 24) + (parts[1] << 16) + (parts[2] << 8) + parts[3];
  };

  const formatMacAddress = (mac: string): string => {
    // Remove all non-hex characters and convert to uppercase
    const cleaned = mac.toUpperCase().replace(/[^0-9A-F]/g, '');
    
    // Insert colons every two characters
    const formatted = cleaned.match(/.{1,2}/g)?.join(':') || cleaned;
    
    return formatted;
  };

  const handleMacChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatMacAddress(e.target.value);
    setFormData(prev => ({
      ...prev,
      mac_address: formatted,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    if (!validateIP(formData.ip_address)) {
      if (!error) setError('Invalid IP address');
      return;
    }
    
    setLoading(true);

    try {
      if (staticIP) {
        // Update existing IP
        const updateData = {
          device_name: formData.device_name,
          device_type: formData.device_type,
          mac_address: formData.mac_address,
          description: formData.description,
        };
        await staticIpApi.update(staticIP.id, updateData);
      } else {
        // Create new IP assignment
        await staticIpApi.create(formData);
      }
      onSubmit();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save static IP');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const generateAvailableIPs = (): string[] => {
    if (!ipRange.static_start || !ipRange.static_end) return [];
    
    const available: string[] = [];
    const startNum = ipToNumber(ipRange.static_start);
    const endNum = ipToNumber(ipRange.static_end);
    
    for (let i = startNum; i <= endNum && available.length < 10; i++) {
      const ip = numberToIP(i);
      if (!existingIPs.includes(ip)) {
        available.push(ip);
      }
    }
    
    return available;
  };

  const numberToIP = (num: number): string => {
    return [
      (num >>> 24) & 0xFF,
      (num >>> 16) & 0xFF,
      (num >>> 8) & 0xFF,
      num & 0xFF
    ].join('.');
  };

  const availableIPs = !staticIP ? generateAvailableIPs() : [];

  return (
    <div className="modal-overlay">
      <div className="modal modal-lg">
        <div className="modal-header">
          <h2>{staticIP ? 'Edit Static IP Assignment' : 'Assign Static IP'}</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="ip_address">IP Address*</label>
            {staticIP ? (
              <input
                type="text"
                id="ip_address"
                name="ip_address"
                value={formData.ip_address}
                disabled
                className="disabled-input"
              />
            ) : (
              <>
                <input
                  type="text"
                  id="ip_address"
                  name="ip_address"
                  value={formData.ip_address}
                  onChange={handleChange}
                  placeholder="e.g., 192.168.1.10"
                  required
                />
                {availableIPs.length > 0 && (
                  <div className="available-ips">
                    <small>Available IPs:</small>
                    <div className="ip-suggestions">
                      {availableIPs.map(ip => (
                        <button
                          key={ip}
                          type="button"
                          className="ip-suggestion"
                          onClick={() => setFormData(prev => ({ ...prev, ip_address: ip }))}
                        >
                          {ip}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="device_name">Device Name</label>
            <input
              type="text"
              id="device_name"
              name="device_name"
              value={formData.device_name}
              onChange={handleChange}
              placeholder="e.g., Web Server 01"
            />
          </div>

          <div className="form-group">
            <label htmlFor="device_type">Device Type</label>
            <select
              id="device_type"
              name="device_type"
              value={formData.device_type}
              onChange={handleChange}
            >
              <option value="">Select type...</option>
              {DEVICE_TYPES.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="mac_address">MAC Address</label>
            <input
              type="text"
              id="mac_address"
              name="mac_address"
              value={formData.mac_address}
              onChange={handleMacChange}
              placeholder="e.g., 00:11:22:33:44:55"
              maxLength={17}
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              placeholder="Additional notes about this device"
            />
          </div>

          {error && <div className="error">{error}</div>}

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving...' : staticIP ? 'Update' : 'Assign'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StaticIPForm;